import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { api } from '../services/apiService';
import './PaymentPage.css';
import Footer from '../components/Footer';
import Header from '../components/Header';

const PaymentPage = () => {
  const { userPhone, userId } = useAuth();
  const { cartItems, getTotalPrice, clearCart, selectedDateTime } = useCart();
  const navigate = useNavigate();
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [isRazorpayLoaded, setIsRazorpayLoaded] = useState(false);

  useEffect(() => {
    const existingScript = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');

    if (existingScript) {
      setIsRazorpayLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => setIsRazorpayLoaded(true);
    script.onerror = () => setOrderError('Razorpay checkout could not be loaded. Please try again.');
    document.body.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    // Redirect back if no date/time selected or cart is empty
    if (cartItems.length === 0) {
      navigate('/home');
      return;
    }
    if (!selectedDateTime || !selectedDateTime.address) {
      // If no date/time or address selected, redirect back to home
      // User should go through checkout flow
      navigate('/home');
    }
  }, [selectedDateTime, cartItems.length, navigate]);
  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderError, setOrderError] = useState('');

  const handlePayment = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    setOrderError('');

    if (!window.Razorpay || !isRazorpayLoaded || !process.env.REACT_APP_RAZORPAY_KEY_ID) {
      setOrderError('Razorpay is not configured. Please contact support or try again later.');
      setIsProcessing(false);
      return;
    }

    try {
      const razorpayOrder = await api.post('/create-order', {
        amount: Math.round(getTotalPrice() * 100),
        currency: 'INR',
        receipt: `myhelpz_${Date.now()}`,
      });

      const razorpayOptions = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: razorpayOrder.amount,
        currency: razorpayOrder.currency,
        name: 'MYHELPZ Technologies',
        description: 'Service booking payment',
        order_id: razorpayOrder.order_id,
        handler: async function (response) {
          try {
            // Create app order first so server can persist payment atomically
            const orderPayload = {
              user_id: userId,
              scheduled_date: selectedDateTime.date,
              scheduled_time: selectedDateTime.timeSlot,
              scheduled_time_label: selectedDateTime.timeLabel,
              address: selectedDateTime.address,
              subtotal: getTotalPrice(),
              total_amount: getTotalPrice(),
              payment_method: paymentMethod,
              payment_status: 'pending',
              status: 'confirmed',
              work_status: 'not_started',
            };

            const orderRes = await api.post('/orders', orderPayload);
            const orderId = orderRes.data?.id;

            if (!orderId) throw new Error('Failed to create order before payment verification.');

            // Verify payment and let server persist payment record and update the order
            const verification = await api.post('/verify-payment', {
              app_order_id: orderId,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            console.log('Payment verification response:', verification);

            if (!verification.success) {
              throw new Error(verification.error || 'Payment verification failed.');
            }

            // Persist order items after payment recorded
            await Promise.all(
              cartItems.map(item =>
                api.post('/order-items', {
                  order_id: orderId,
                  service_id: item.serviceId,
                  service_name: item.name,
                  description: item.description || '',
                  category: item.category,
                  quantity: item.quantity,
                  price_per_hour: item.pricePerHour,
                  total_price: item.pricePerHour * item.quantity,
                })
              )
            );

            clearCart();
            navigate('/my-orders', { state: { orderBooked: true } });
          } catch (err) {
            console.error('Order creation failed:', err);
            setOrderError('Payment failed: ' + (err.message || 'Please try again.'));
          } finally {
            setIsProcessing(false);
          }
        },
        prefill: {
          contact: userPhone || '',
        },
        notes: {
          userPhone: userPhone || '',
        },
        theme: {
          color: '#2d7ff9',
        },
        modal: {
          ondismiss: () => {
            setOrderError('Payment was cancelled. Please try again if you want to complete the booking.');
            setIsProcessing(false);
          },
        },
      };

      const razorpayInstance = new window.Razorpay(razorpayOptions);
      razorpayInstance.on('payment.failed', function (response) {
        console.error('Razorpay payment failed:', response.error);
        setOrderError(response.error && response.error.description ? response.error.description : 'Payment failed. Please try again.');
        setIsProcessing(false);
      });

      razorpayInstance.open();
    } catch (err) {
      console.error('Razorpay checkout failed:', err);
      setOrderError('Payment failed: ' + (err.message || 'Please try again.'));
      setIsProcessing(false);
    }
  };

  const handleCardInputChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;
    
    // Format card number with spaces
    if (name === 'cardNumber') {
      formattedValue = value.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();
      if (formattedValue.length > 19) formattedValue = formattedValue.slice(0, 19);
    }
    
    // Format expiry date
    if (name === 'expiryDate') {
      formattedValue = value.replace(/\D/g, '').replace(/(\d{2})(\d)/, '$1/$2');
      if (formattedValue.length > 5) formattedValue = formattedValue.slice(0, 5);
    }
    
    // Format CVV (numbers only)
    if (name === 'cvv') {
      formattedValue = value.replace(/\D/g, '');
      if (formattedValue.length > 3) formattedValue = formattedValue.slice(0, 3);
    }
    
    setCardDetails(prev => ({
      ...prev,
      [name]: formattedValue
    }));
  };

  return (
    <div className="payment-page">
      <div className="payment-container">
        <Header leftContent={
          <button className="back-btn" onClick={() => navigate(-1)}>← Back</button>
        } />

        <main className="payment-content">
          <div className="payment-card">
            <h2>Payment Details</h2>
            
            <div className="order-summary">
              <h3>Order Summary</h3>
              {selectedDateTime && (
                <div className="datetime-info">
                  <div className="datetime-item">
                    <span className="datetime-label">📅 Scheduled Date:</span>
                    <span className="datetime-value">
                      {new Date(selectedDateTime.date).toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </span>
                  </div>
                  <div className="datetime-item">
                    <span className="datetime-label">🕐 Time Slot:</span>
                    <span className="datetime-value">{selectedDateTime.timeLabel}</span>
                  </div>
                  {selectedDateTime.address && (
                    <div className="address-display-section">
                      <div className="datetime-label" style={{ marginBottom: '10px' }}>
                        <span className="label-icon">📍</span>
                        Service Address
                      </div>
                      <div className="address-display">
                        {selectedDateTime.address.streetAddress && (
                          <div className="address-line">{selectedDateTime.address.streetAddress}</div>
                        )}
                        {(selectedDateTime.address.floor || selectedDateTime.address.flatNumber) && (
                          <div className="address-line">
                            {selectedDateTime.address.floor && `${selectedDateTime.address.floor}, `}
                            {selectedDateTime.address.flatNumber}
                          </div>
                        )}
                        {selectedDateTime.address.apartment && (
                          <div className="address-line">{selectedDateTime.address.apartment}</div>
                        )}
                        {selectedDateTime.address.city && (
                          <div className="address-line">{selectedDateTime.address.city}</div>
                        )}
                        {selectedDateTime.address.pin && (
                          <div className="address-line">PIN: {selectedDateTime.address.pin}</div>
                        )}
                        {selectedDateTime.address.landmark && (
                          <div className="address-line" style={{ fontStyle: 'italic', color: '#666' }}>
                            Landmark: {selectedDateTime.address.landmark}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="summary-items">
                {cartItems.map((item) => (
                  <div key={item.id} className="summary-item">
                    <span className="item-name">{item.name} × {item.quantity}</span>
                    <span className="item-price">₹{item.pricePerHour * item.quantity}</span>
                  </div>
                ))}
              </div>
              <div className="summary-total">
                <span>Total Amount:</span>
                <span>₹{getTotalPrice()}</span>
              </div>
            </div>

            <div className="user-info-section">
              <p><strong>Phone:</strong> {userPhone}</p>
            </div>

            <form className="payment-form" onSubmit={handlePayment}>
              

              <div className="payment-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="pay-btn"
                  disabled={isProcessing || cartItems.length === 0}
                >
                  {isProcessing ? 'Processing...' : `Pay ₹${getTotalPrice()}`}
                </button>
              </div>
            </form>
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default PaymentPage;

